import { observable, makeObservable } from 'mobx';
import { appConfigStore } from 'Models/AppConfigStore';
import { bookingType } from 'Models/Enums';
import moment from 'moment';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import { formatStandardTime } from 'Util/_HumanWritten/TimeUtils';
import * as Models from 'Models/Entities';
import { CapacityProfileDto } from 'Models/Dtos/CapacityProfileDtos';
import * as AttrUtils from 'Util/AttributeUtils';
import type { IWeightSpaceCapacity } from 'Models/Shared/Capacity/IWeightSpaceCapacity';
import type { IVehicleSpaceCapacity } from 'Models/Shared/Capacity/IVehicleSpaceCapacity';
import type { IPassengerSpaceCapacity } from 'Models/Shared/Capacity/IPassengerSpaceCapacity';
import { formatDateMonth } from 'Util/_HumanWritten/DateUtils';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
import { getDayOfTheWeek } from 'Util/_HumanWritten/DayOfWeekUtils';

export interface CommunicationTabContactsCount {
	mobileNumberCount: number;
	emailAddressesCount: number;
}

interface UserList {
	userTypes: string[];
	userDisplayNameTypes: string[];
}

export interface ViewAuthorisationConfigurations {
	include: UserList;
	exclude: UserList;
}

export interface IFerryTripEntityAttributes extends IModelAttributes {
	name: string;
	startDate: Date;
	endDate: Date;
	startTime: Date;
	endTime: Date;
	adultPassengerPrice: number;
	childPassengerPrice: number;
	infantPassengerPrice: number;
	legacyId: string;
	disabled: boolean;
	hidden: boolean;
	checkInFinalised: boolean;
	priceD: number;
	priceE: number;
	priceF: number;
	priceG: number;
	priceH: number;
	showAdditionalIcon: boolean;
	vehicleBookingsClosed: boolean;
	passengerBookingsClosed: boolean;
	freightBookingsClosed: boolean;
	eventBookingsClosed: boolean;
	preventAutomaticClosing: boolean;
	alterationRulesDisabled?: boolean;
	cancellationRulesDisabled?: boolean;
	helmId: string;
	thankYouEmailSent?: boolean;
	configuration?: string;

	alterations: Array<Models.AlterationEntity | Models.IAlterationEntityAttributes>;
	eventDetailId?: string;
	eventDetail?: Models.EventDetailEntity | Models.IEventDetailEntityAttributes;
	ferryId?: string;
	ferry?: Models.FerryEntity | Models.IFerryEntityAttributes;
	capacityProfileId?: string;
	capacityProfile?: CapacityProfileDto | null;
	routeId?: string;
	route?: Models.RouteEntity | Models.IRouteEntityAttributes;
	notes: Array<Models.NotesEntity | Models.INotesEntityAttributes>;
	prices: Array<Models.PriceEntity | Models.IPriceEntityAttributes>;
	smsDeliveryRecords: Array<Models.SmsDeliveryRecordEntity | Models.ISmsDeliveryRecordEntityAttributes>;
	tripStops: Array<Models.TripStopEntity | Models.ITripStopEntityAttributes>;
	waitlistItems: Array<Models.WaitlistItemEntity | Models.IWaitlistItemEntityAttributes>;
	pricingStrategyProfileId?: string;
	pricingStrategyProfile?: Models.PricingStrategyProfileEntity | Models.IPricingStrategyProfileEntityAttributes;
	departureDateTime: Date | null;
	capacity?: number | null;
	arrivalDateTime: Date | null;
	passengerSpaceCapacity?: IPassengerSpaceCapacity | null;
	vehicleSpaceCapacity?: IVehicleSpaceCapacity | null;
	weightSpaceCapacity?: IWeightSpaceCapacity | null;

	alterationssCount?: number | null;
	recurrenceId?: string;
	isDraft?: boolean;
	importId?: string | null;
	onWaitlist?: boolean;
	waitlistFull?: boolean;
	standbyFull?: boolean;
}
@entity('FerryTripEntity', 'Ferry Trip')
export default class FerryTripEntity extends Model implements IFerryTripEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<FerryTripEntity, string>()
	public name: string;

	@observable
	@attribute<FerryTripEntity, Date>({ serialise: (e, a) => AttrUtils.serialiseDate(a) })
	public startDate: Date;

	@observable
	@attribute<FerryTripEntity, Date>({ serialise: (e, a) => AttrUtils.serialiseDate(a) })
	public endDate: Date;

	@observable
	@attribute<FerryTripEntity, Date>({ serialise: (e, a) => AttrUtils.serialiseTime(a) })
	public startTime: Date;

	@observable
	@attribute<FerryTripEntity, Date>({ serialise: (e, a) => AttrUtils.serialiseTime(a) })
	public endTime: Date;

	@observable
	@attribute<FerryTripEntity, number>()
	public adultPassengerPrice: number;

	@observable
	@attribute<FerryTripEntity, number>()
	public childPassengerPrice: number;

	@observable
	@attribute<FerryTripEntity, number>()
	public infantPassengerPrice: number;

	@observable
	@attribute<FerryTripEntity, number>()
	public legacyId: string;

	@observable
	@attribute<FerryTripEntity, boolean>()
	public disabled: boolean;

	@observable
	@attribute<FerryTripEntity, boolean>()
	public thankYouEmailSent?: boolean;

	/**
	 * Whether or a not a ferry trip is hidden from non-staff users.
	 */
	@observable
	@attribute<FerryTripEntity, boolean>()
	public hidden: boolean;

	@observable
	@attribute<FerryTripEntity, boolean>()
	public checkInFinalised: boolean;

	@observable
	@attribute<FerryTripEntity, string>()
	public configuration?: string;

	@observable
	@attribute<FerryTripEntity, number>()
	public priceD: number;

	@observable
	@attribute<FerryTripEntity, number>()
	public priceE: number;

	@observable
	@attribute<FerryTripEntity, number>()
	public priceF: number;

	@observable
	@attribute<FerryTripEntity, number>()
	public priceG: number;

	@observable
	@attribute<FerryTripEntity, number>()
	public priceH: number;

	/**
	 * Whether or not to show the additional icon for ferry trips.
	 */
	@observable
	@attribute<FerryTripEntity, boolean>()
	public showAdditionalIcon: boolean;

	/**
	 * Whether or not the ferry trip is closed from taking customer vehicle bookings.
	 */
	@observable
	@attribute<FerryTripEntity, boolean>()
	public vehicleBookingsClosed: boolean;

	/**
	 * Whether or not the ferry trip is closed from taking customer passenger bookings.
	 */
	@observable
	@attribute<FerryTripEntity, boolean>()
	public passengerBookingsClosed: boolean;

	/**
	 * Whether or not the ferry trip is closed from taking customer freight bookings.
	 */
	@observable
	@attribute<FerryTripEntity, boolean>()
	public freightBookingsClosed: boolean;

	/**
	 * Whether or not the ferry trip is closed from taking customer event bookings.
	 */
	@observable
	@attribute<FerryTripEntity, boolean>()
	public eventBookingsClosed: boolean;

	/**
	 * If this is checked, the ferry trip will not automatically close in the background job again.
	 */
	@observable
	@attribute<FerryTripEntity, boolean>()
	public preventAutomaticClosing: boolean;

	/**
	 * Whether bookings moved from or to this ferry trip are charged no alteration fee.
	 */
	@observable
	@attribute<FerryTripEntity, boolean>()
	public alterationRulesDisabled?: boolean;

	/**
	 * Whether bookings cancelled from this ferry trip are charged no cancellation fee.
	 */
	@observable
	@attribute<FerryTripEntity, boolean>()
	public cancellationRulesDisabled?: boolean;

	/**
	 * The relationship between an alteration and a ferry trip
	 */
	@observable
	@attribute({ isReference: true, manyReference: true })
	public alterations: Models.AlterationEntity[] = [];

	@observable
	@attribute()
	public eventDetailId?: string;

	@observable
	@attribute()
	public recurrenceId: string;

	/**
	 * A draft trip: hidden from customers, and from staff it is not shared with, until it is
	 * published. Read by the server on create (the "Save as draft" option); after that only the
	 * publish endpoint changes it.
	 */
	@observable
	@attribute<FerryTripEntity, boolean>()
	public isDraft?: boolean;

	/** The import batch a draft trip came from. Null for a trip created by hand. Read-only here. */
	@observable
	@attribute()
	public importId?: string | null;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public eventDetail: Models.EventDetailEntity;

	@observable
	@attribute()
	public ferryId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public ferry: Models.FerryEntity;

	/**
	 * The capacity profile the trip sells against. Sent on save: null with a changed ferry takes the
	 * new ferry's default.
	 */
	@observable
	@attribute()
	public capacityProfileId?: string;

	/** The profile's numbers, as the server loaded them. Read-only; not sent on save. */
	@observable
	public capacityProfile?: CapacityProfileDto | null;

	@observable
	@attribute()
	public routeId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public route: Models.RouteEntity;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public notes: Models.NotesEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public prices: Models.PriceEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public smsDeliveryRecords: Models.SmsDeliveryRecordEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public tripStops: Models.TripStopEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public waitlistItems: Models.WaitlistItemEntity[] = [];

	@observable
	@attribute<FerryTripEntity, string>()
	public helmId: string;

	@observable
	@attribute()
	public pricingStrategyProfileId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public pricingStrategyProfile: Models.PricingStrategyProfileEntity;

	public onWaitlist?: boolean;

	public waitlistFull?: boolean;

	/** The trip's standby list has reached `Standby.MaxCountPerTrip`. Always false when standby is off. */
	public standbyFull?: boolean;

	constructor(attributes?: Partial<IFerryTripEntityAttributes>) {
		super(attributes);

		this.alterations = [];
		this.notes = [];
		this.prices = [];
		this.smsDeliveryRecords = [];
		this.tripStops = [];
		this.waitlistItems = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IFerryTripEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.name !== undefined) {
				this.name = attributes.name;
			}
			if (attributes.startDate !== undefined) {
				if (attributes.startDate === null) {
					this.startDate = attributes.startDate;
				} else {
					this.startDate = moment(attributes.startDate).toDate();
				}
			}
			if (attributes.endDate !== undefined) {
				if (attributes.endDate === null) {
					this.endDate = attributes.endDate;
				} else {
					this.endDate = moment(attributes.endDate).toDate();
				}
			}
			if (attributes.startTime !== undefined) {
				if (attributes.startTime === null) {
					this.startTime = attributes.startTime;
				} else {
					this.startTime = moment(attributes.startTime, 'HH:mm:ss').toDate();
				}
			}
			if (attributes.endTime !== undefined) {
				if (attributes.endTime === null) {
					this.endTime = attributes.endTime;
				} else {
					this.endTime = moment(attributes.endTime, 'HH:mm:ss').toDate();
				}
			}
			if (attributes.adultPassengerPrice !== undefined) {
				this.adultPassengerPrice = attributes.adultPassengerPrice;
			}
			if (attributes.childPassengerPrice !== undefined) {
				this.childPassengerPrice = attributes.childPassengerPrice;
			}
			if (attributes.infantPassengerPrice !== undefined) {
				this.infantPassengerPrice = attributes.infantPassengerPrice;
			}
			if (attributes.legacyId !== undefined) {
				this.legacyId = attributes.legacyId;
			}
			if (attributes.disabled !== undefined) {
				this.disabled = attributes.disabled;
			}
			if (isNotNullOrUndefined(attributes.thankYouEmailSent)) {
				this.thankYouEmailSent = attributes.thankYouEmailSent;
			}
			if (attributes.hidden !== undefined) {
				this.hidden = attributes.hidden;
			}
			if (attributes.checkInFinalised !== undefined) {
				this.checkInFinalised = attributes.checkInFinalised;
			}
			if (attributes.priceD !== undefined) {
				this.priceD = attributes.priceD;
			}
			if (attributes.priceE !== undefined) {
				this.priceE = attributes.priceE;
			}
			if (attributes.priceF !== undefined) {
				this.priceF = attributes.priceF;
			}
			if (attributes.priceG !== undefined) {
				this.priceG = attributes.priceG;
			}
			if (attributes.priceH !== undefined) {
				this.priceH = attributes.priceH;
			}
			if (attributes.showAdditionalIcon !== undefined) {
				this.showAdditionalIcon = attributes.showAdditionalIcon;
			}
			if (attributes.vehicleBookingsClosed !== undefined) {
				this.vehicleBookingsClosed = attributes.vehicleBookingsClosed;
			}
			if (attributes.passengerBookingsClosed !== undefined) {
				this.passengerBookingsClosed = attributes.passengerBookingsClosed;
			}
			if (attributes.freightBookingsClosed !== undefined) {
				this.freightBookingsClosed = attributes.freightBookingsClosed;
			}
			if (attributes.eventBookingsClosed !== undefined) {
				this.eventBookingsClosed = attributes.eventBookingsClosed;
			}
			if (attributes.preventAutomaticClosing !== undefined) {
				this.preventAutomaticClosing = attributes.preventAutomaticClosing;
			}
			if (attributes.alterationRulesDisabled !== undefined) {
				this.alterationRulesDisabled = attributes.alterationRulesDisabled;
			}
			if (attributes.cancellationRulesDisabled !== undefined) {
				this.cancellationRulesDisabled = attributes.cancellationRulesDisabled;
			}
			if (attributes.recurrenceId !== undefined) {
				this.recurrenceId = attributes.recurrenceId;
			}
			if (attributes.isDraft !== undefined) {
				this.isDraft = attributes.isDraft;
			}
			if (attributes.importId !== undefined) {
				this.importId = attributes.importId;
			}
			if (attributes.helmId !== undefined) {
				this.helmId = attributes.helmId;
			}
			if (attributes.alterations !== undefined && Array.isArray(attributes.alterations)) {
				if (!this.alterations) {
					this.alterations = [];
				}
				for (const model of attributes.alterations) {
					if (model instanceof Models.AlterationEntity) {
						this.alterations.push(model);
					} else {
						this.alterations.push(new Models.AlterationEntity(model));
					}
				}
			}
			if (attributes.eventDetailId !== undefined) {
				this.eventDetailId = attributes.eventDetailId;
			}
			if (attributes.eventDetail !== undefined) {
				if (attributes.eventDetail === null) {
					this.eventDetail = attributes.eventDetail;
				} else if (attributes.eventDetail instanceof Models.EventDetailEntity) {
					this.eventDetail = attributes.eventDetail;
					this.eventDetailId = attributes.eventDetail.id;
				} else {
					this.eventDetail = new Models.EventDetailEntity(attributes.eventDetail);
					this.eventDetailId = this.eventDetail.id;
				}
			}
			if (attributes.ferryId !== undefined) {
				this.ferryId = attributes.ferryId;
			}
			if (attributes.capacityProfileId !== undefined) {
				this.capacityProfileId = attributes.capacityProfileId;
			}
			if (attributes.capacityProfile !== undefined) {
				this.capacityProfile = attributes.capacityProfile;
			}
			if (attributes.ferry !== undefined) {
				if (attributes.ferry === null) {
					this.ferry = attributes.ferry;
				} else if (attributes.ferry instanceof Models.FerryEntity) {
					this.ferry = attributes.ferry;
					this.ferryId = attributes.ferry.id;
				} else {
					this.ferry = new Models.FerryEntity(attributes.ferry);
					this.ferryId = this.ferry.id;
				}
			}
			if (attributes.routeId !== undefined) {
				this.routeId = attributes.routeId;
			}
			if (attributes.route !== undefined) {
				if (attributes.route === null) {
					this.route = attributes.route;
				} else if (attributes.route instanceof Models.RouteEntity) {
					this.route = attributes.route;
					this.routeId = attributes.route.id;
				} else {
					this.route = new Models.RouteEntity(attributes.route);
					this.routeId = this.route.id;
				}
			}
			if (attributes.notes !== undefined && Array.isArray(attributes.notes)) {
				if (!this.notes) {
					this.notes = [];
				}
				for (const model of attributes.notes) {
					if (model instanceof Models.NotesEntity) {
						this.notes.push(model);
					} else {
						this.notes.push(new Models.NotesEntity(model));
					}
				}
			}
			if (attributes.prices !== undefined && Array.isArray(attributes.prices)) {
				if (!this.prices) {
					this.prices = [];
				}
				for (const model of attributes.prices) {
					if (model instanceof Models.PriceEntity) {
						this.prices.push(model);
					} else {
						this.prices.push(new Models.PriceEntity(model));
					}
				}
			}
			if (attributes.smsDeliveryRecords !== undefined && Array.isArray(attributes.smsDeliveryRecords)) {
				if (!this.smsDeliveryRecords) {
					this.smsDeliveryRecords = [];
				}
				for (const model of attributes.smsDeliveryRecords) {
					if (model instanceof Models.SmsDeliveryRecordEntity) {
						this.smsDeliveryRecords.push(model);
					} else {
						this.smsDeliveryRecords.push(new Models.SmsDeliveryRecordEntity(model));
					}
				}
			}
			if (attributes.tripStops !== undefined && Array.isArray(attributes.tripStops)) {
				if (!this.tripStops) {
					this.tripStops = [];
				}
				for (const model of attributes.tripStops) {
					if (model instanceof Models.TripStopEntity) {
						this.tripStops.push(model);
					} else {
						this.tripStops.push(new Models.TripStopEntity(model));
					}
				}
			}
			if (attributes.waitlistItems !== undefined && Array.isArray(attributes.waitlistItems)) {
				if (!this.waitlistItems) {
					this.waitlistItems = [];
				}
				for (const model of attributes.waitlistItems) {
					if (model instanceof Models.WaitlistItemEntity) {
						this.waitlistItems.push(model);
					} else {
						this.waitlistItems.push(new Models.WaitlistItemEntity(model));
					}
				}
			}
			if (attributes.pricingStrategyProfileId !== undefined) {
				this.pricingStrategyProfileId = attributes.pricingStrategyProfileId;
			}
			if (attributes.pricingStrategyProfile !== undefined) {
				if (attributes.pricingStrategyProfile === null) {
					this.pricingStrategyProfile = attributes.pricingStrategyProfile;
				} else if (attributes.pricingStrategyProfile instanceof Models.PricingStrategyProfileEntity) {
					this.pricingStrategyProfile = attributes.pricingStrategyProfile;
					this.pricingStrategyProfileId = attributes.pricingStrategyProfile.id;
				} else {
					this.pricingStrategyProfile = new Models.PricingStrategyProfileEntity(
						attributes.pricingStrategyProfile,
					);
					this.pricingStrategyProfileId = this.pricingStrategyProfile.id;
				}
			}
			if (isNotNullOrUndefined(attributes.capacity)) {
				this.capacity = attributes.capacity;
			}

			if (isNotNullOrUndefined(attributes.passengerSpaceCapacity)) {
				const passengerSpaceCapacity: IPassengerSpaceCapacity = {};
				if (attributes.passengerSpaceCapacity.used !== undefined) {
					passengerSpaceCapacity.used = attributes.passengerSpaceCapacity.used;
				}
				if (attributes.passengerSpaceCapacity.remaining !== undefined) {
					passengerSpaceCapacity.remaining = attributes.passengerSpaceCapacity.remaining;
				}
				if (attributes.passengerSpaceCapacity.overbookingAllowanceUsed !== undefined) {
					passengerSpaceCapacity.overbookingAllowanceUsed =
						attributes.passengerSpaceCapacity.overbookingAllowanceUsed;
				}
				if (attributes.passengerSpaceCapacity.overbookingAllowanceRemaining !== undefined) {
					passengerSpaceCapacity.overbookingAllowanceRemaining =
						attributes.passengerSpaceCapacity.overbookingAllowanceRemaining;
				}
				this.passengerSpaceCapacity = passengerSpaceCapacity;
			}

			if (isNotNullOrUndefined(attributes.vehicleSpaceCapacity)) {
				const vehicleSpaceCapacity: IVehicleSpaceCapacity = {};
				if (attributes.vehicleSpaceCapacity.used !== undefined) {
					vehicleSpaceCapacity.used = attributes.vehicleSpaceCapacity.used;
				}
				if (attributes.vehicleSpaceCapacity.remaining !== undefined) {
					vehicleSpaceCapacity.remaining = attributes.vehicleSpaceCapacity.remaining;
				}
				if (attributes.vehicleSpaceCapacity.overbookingAllowanceUsed !== undefined) {
					vehicleSpaceCapacity.overbookingAllowanceUsed =
						attributes.vehicleSpaceCapacity.overbookingAllowanceUsed;
				}
				if (attributes.vehicleSpaceCapacity.overbookingAllowanceRemaining !== undefined) {
					vehicleSpaceCapacity.overbookingAllowanceRemaining =
						attributes.vehicleSpaceCapacity.overbookingAllowanceRemaining;
				}
				if (attributes.vehicleSpaceCapacity.trailerUsed !== undefined) {
					vehicleSpaceCapacity.trailerUsed = attributes.vehicleSpaceCapacity.trailerUsed;
				}
				if (attributes.vehicleSpaceCapacity.trailerRemaining !== undefined) {
					vehicleSpaceCapacity.trailerRemaining = attributes.vehicleSpaceCapacity.trailerRemaining;
				}
				this.vehicleSpaceCapacity = vehicleSpaceCapacity;
			}

			if (isNotNullOrUndefined(attributes.weightSpaceCapacity)) {
				const weightSpaceCapacity: IWeightSpaceCapacity = {};
				if (attributes.weightSpaceCapacity.used !== undefined) {
					weightSpaceCapacity.used = attributes.weightSpaceCapacity.used;
				}
				if (attributes.weightSpaceCapacity.remaining !== undefined) {
					weightSpaceCapacity.remaining = attributes.weightSpaceCapacity.remaining;
				}
				this.weightSpaceCapacity = weightSpaceCapacity;
			}

			if (isNotNullOrUndefined(attributes.alterationssCount)) {
				this.alterationssCount = attributes.alterationssCount;
			}
			if (isNotNullOrUndefined(attributes.configuration)) {
				this.configuration = attributes.configuration;
			}
			if (isNotNullOrUndefined(attributes?.onWaitlist)) {
				this.onWaitlist = attributes.onWaitlist ?? false;
			}
			if (isNotNullOrUndefined(attributes?.waitlistFull)) {
				this.waitlistFull = attributes?.waitlistFull ?? false;
			}
			if (isNotNullOrUndefined(attributes?.standbyFull)) {
				this.standbyFull = attributes?.standbyFull ?? false;
			}

			// Combine startDate + startTime into a single Date object for startDate
			// This is needed because the backend returns DateOnly and TimeOnly separately
			if (
				this.startDate &&
				this.startTime &&
				!Number.isNaN(this.startDate.getTime()) &&
				!Number.isNaN(this.startTime.getTime())
			) {
				this.startDate = new Date(
					this.startDate.getFullYear(),
					this.startDate.getMonth(),
					this.startDate.getDate(),
					this.startTime.getHours(),
					this.startTime.getMinutes(),
					this.startTime.getSeconds(),
				);
			}

			// Combine endDate + endTime into a single Date object for endDate
			if (this.endDate && this.endTime) {
				this.endDate = new Date(
					this.endDate.getFullYear(),
					this.endDate.getMonth(),
					this.endDate.getDate(),
					this.endTime.getHours(),
					this.endTime.getMinutes(),
					this.endTime.getSeconds(),
				);
			}
		}
	}

	public capacity: number;

	public get departureDateTime(): Date {
		if (!this.startDate || !this.startTime) {
			return new Date();
		}
		return new Date(
			this.startDate.getFullYear(),
			this.startDate.getMonth(),
			this.startDate.getDate(),
			this.startTime.getHours(),
			this.startTime.getMinutes(),
			this.startTime.getSeconds(),
		);
	}

	public set departureDateTime(value) {
		this.startDate = value;
		this.startTime = value;
	}

	/**
	 * The trip's own arrival at its last stop when one is stored, otherwise departure + the route's duration.
	 * A stored arrival is only kept where it differs from the route's timing, so a trip nobody changed follows
	 * its route when the route's duration is edited.
	 */
	public get arrivalDateTime(): Date {
		const storedArrival = this.lastTripStop()?.arrivalDateTime;
		if (storedArrival) {
			return moment(storedArrival).toDate();
		}
		if (!this.startDate || !this.startTime || !this.route) {
			return new Date();
		}
		const date = moment(this.departureDateTime).add(this.route.duration, 'minutes');
		return date.toDate();
	}

	/**
	 * The trip stop at the route's last stop, by route order — undefined when the stops, or the order needed to
	 * tell which is last, are not loaded.
	 */
	private lastTripStop(): Models.TripStopEntity | undefined {
		if (!this.tripStops?.length) {
			return undefined;
		}
		const routeOrder = new Map((this.route?.stops ?? []).map(stop => [stop.id, stop.order]));
		const orderOf = (tripStop: Models.TripStopEntity) => routeOrder.get(tripStop.stopId) ?? tripStop.stop?.order;
		if (this.tripStops.some(tripStop => orderOf(tripStop) == null)) {
			return undefined;
		}
		return this.tripStops.reduce((last, tripStop) => (orderOf(tripStop)! > orderOf(last)! ? tripStop : last));
	}

	/**
	 * Format departure date time as 12:30pm.
	 */
	public get departTimeFormatted(): string | null {
		if (!this.startTime) {
			return null;
		}
		return formatStandardTime(this.startTime);
	}

	/**
	 * Format arrival date time as 12:30pm.
	 */
	public get arrivalTimeFormatted(): string | null {
		if (!this.arrivalDateTime) {
			return null;
		}
		return formatStandardTime(this.arrivalDateTime);
	}

	/**
	 * Format arrival date time as 10:00am - 12:30pm.
	 */
	public get tripTimeFormatted(): string | null {
		return `${this.departTimeFormatted ?? ''} - ${this.arrivalTimeFormatted ?? ''}`;
	}

	public passengerSpaceCapacity: IPassengerSpaceCapacity;

	public vehicleSpaceCapacity: IVehicleSpaceCapacity;

	public weightSpaceCapacity: IWeightSpaceCapacity;

	public getConfigurationJsonAsDto(): ViewAuthorisationConfigurations {
		let parsedConfig: any;
		if (isNotNullOrUndefined(this.configuration)) {
			try {
				parsedConfig = JSON.parse(this.configuration);
			} catch (e) {
				console.error('Failed to parse configuration:', e);
				// Return no inclusions or exclusions (which assumes everyone can see the trip)
				parsedConfig = {
					include: { userTypes: [], userDisplayNameTypes: [] },
					exclude: { userTypes: [], userDisplayNameTypes: [] },
				};
			}

			// Helper function to ensure UserList structure
			const parseUserList = (userListData: any): UserList => {
				return {
					userTypes: Array.isArray(userListData?.userTypes) ? userListData.userTypes : [],
					userDisplayNameTypes: Array.isArray(userListData?.userDisplayNameTypes)
						? userListData.userDisplayNameTypes
						: [],
				};
			};

			return {
				include: parseUserList(parsedConfig.include),
				exclude: parseUserList(parsedConfig.exclude),
			};
		}
		return {
			include: { userTypes: [], userDisplayNameTypes: [] },
			exclude: { userTypes: [], userDisplayNameTypes: [] },
		};
	}

	public setConfigurationFromDto(config: ViewAuthorisationConfigurations): void {
		this.configuration = JSON.stringify(config);
	}

	/**
	 * Custom graphQL field. Returns the number of associated alterations.
	 */
	public alterationssCount: number;

	/**
	 * Example: 10:00 - 11:30am, B > M
	 */
	public getShortDisplayName() {
		return (
			`${formatDateMonth(this.startDate)} ${this.tripTimeFormatted}, ` +
			`${this.route?.departure?.shortName} > ${this.route?.destination?.shortName}`
		);
	}

	/**
	 * Example: 10:00 - 11:30am, Tue 25 Oct 2022
	 */
	public getDisplayTimeDayDate() {
		const momentDateTime = moment(this.departureDateTime);
		return (
			`${this.tripTimeFormatted}, ` +
			`${getDayOfTheWeek(momentDateTime.day(), false)} ` +
			`${momentDateTime.format('DD MMM YYYY')}`
		);
	}

	/**
	 * Example: 10:00 - 11:30am
	 */
	public getTripTimeFormatted() {
		return this.tripTimeFormatted;
	}

	/**
	 * Example: Riverfire
	 */
	public getEventName() {
		return this?.eventDetail?.name ?? '';
	}

	/**
	 * Example:
	 * !fullMonth: 29 Jun 2023
	 * fullMonth: 29 June 2023
	 */
	public getFormattedTripDateLong(fullMonth?: boolean) {
		const formatString = fullMonth ? 'DD MMMM YYYY' : 'DD MMM YYYY';
		return `${moment(this.startDate).format(formatString)}`;
	}

	public vehicleBookingsAreClosed() {
		// const vehicleRequiresPassengers = appConfigStore.vehicleBookingRequiresAdult;
		// const vehicleBookingsClosedBecausePassengerBookingsClosed = this.passengerBookingsClosed && vehicleRequiresPassengers;

		const vehicleBookingsClosed = !appConfigStore.vehicleEnabled || this.vehicleBookingsClosed;

		return vehicleBookingsClosed || this.passengerBookingsClosed;
	}

	public passengerBookingsAreClosed() {
		return this.passengerBookingsClosed;
	}

	public freightBookingsAreClosed() {
		return !appConfigStore.freightEnabled || this.freightBookingsClosed;
	}

	public eventBookingsAreClosed() {
		return !appConfigStore.eventsEnabled || this.eventBookingsClosed;
	}

	public allBookingTypesAreClosed() {
		return this.vehicleBookingsAreClosed() && this.passengerBookingsAreClosed() && this.freightBookingsAreClosed();
	}

	public isClosedForSpecificBookingType(typeOfBooking: bookingType) {
		switch (typeOfBooking) {
			case 'VEHICLE':
				if (this.vehicleBookingsAreClosed()) {
					return true;
				}
				// If passenger bookings are closed, we also need to close vehicle bookings (TEMP solution)
				if (this.passengerBookingsAreClosed()) {
					return true;
				}
				break;
			case 'PASSENGER_ONLY':
				if (this.passengerBookingsAreClosed()) {
					return true;
				}
				break;
			case 'FREIGHT':
				if (this.freightBookingsAreClosed()) {
					return true;
				}
				break;
			case 'EVENT_BOOKING':
				if (this.eventBookingsAreClosed()) {
					return true;
				}
				break;
			default:
				return true;
		}
		return false;
	}
}
